import {
  AccessToken,
  GetKeepLogin,
  GetTimezoneOffset,
  MerchantId,
  RemoveCookiesToken,
  SetCookiesAccessToken,
  SetCookiesRefreshToken,
} from "@/helper/userHelper";
import { i18n } from "@/i18n";
import API from "@/service/Index";
import axios from "axios";

let isRefreshing = false;
let failedQueue = [] as any;

const ProcessQueue = () => {
  const token = AccessToken();
  if (token && failedQueue.length > 0) {
    failedQueue.forEach((prom: any) => {
      prom.resolve();
    });
  }
  failedQueue = [];
};

const ReSendApiByNewAccessToken = (originalConfig: any) => {
  const token = AccessToken();
  originalConfig.headers["Authorization"] = "Bearer " + token;
  return axios(originalConfig)
    .then((resCallback) => {
      return resCallback;
    })
    .catch(() => {
      location.href = `${window.location.origin}/SessionExpire`;
    });
};

const CreateAxiosInstance = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = AccessToken();
      const merchantId = MerchantId();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      if (merchantId) {
        config.headers["merchantId"] = `${merchantId}`;
      }
      config.headers["language"] = i18n.locale;
      config.headers["Cache-Control"] = "no-store, no-cache, max-age=0, must-revalidate, proxy-revalidate";
      config.headers["CDN-Cache-Control"] = "no-store, no-cache, max-age=0, must-revalidate, proxy-revalidate";
      config.headers["timezoneOffset"] = GetTimezoneOffset();

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      const originalConfig = err.config;
      if (err.response && err.response.status === 401) {
        // Access Token was expire
        if (isRefreshing) {
          return new Promise((resolve) => {
            failedQueue.push({ resolve });
          }).then(() => {
            return ReSendApiByNewAccessToken(originalConfig);
          });
        }

        isRefreshing = true;

        return API.AuthenService.DoGetNewAccessTokenByRefreshToken()
          .then((resNewAccessTokenByRefreshToken) => {
            if (resNewAccessTokenByRefreshToken.status.IsSuccess()) {
              const keepLogin = GetKeepLogin();
              SetCookiesAccessToken(resNewAccessTokenByRefreshToken.data.access_token);
              SetCookiesRefreshToken(resNewAccessTokenByRefreshToken.data.refresh_token, keepLogin);
              return ReSendApiByNewAccessToken(originalConfig);
            } else {
              location.href = `${window.location.origin}/SessionExpire`;
            }
          })
          .catch((_error) => {
            RemoveCookiesToken();
            location.href = `${window.location.origin}/SessionExpire`;
          })
          .finally(() => {
            ProcessQueue();
            isRefreshing = false;
          });
      }

      return Promise.reject(err);
    }
  );
  return instance;
};

const CreateAxiosInstanceForNotificationService = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = AccessToken();
      const merchantId = MerchantId();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      if (merchantId) {
        config.headers["merchantId"] = `${merchantId}`;
      }
      config.headers["language"] = i18n.locale;
      config.headers["timezoneOffset"] = GetTimezoneOffset();

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      const originalConfig = err.config;
      if (err.response && err.response.status === 401) {
        // Access Token was expire
        if (isRefreshing) {
          return new Promise((resolve) => {
            failedQueue.push({ resolve });
          }).then(() => {
            return ReSendApiByNewAccessToken(originalConfig);
          });
        }

        isRefreshing = true;

        return API.NotificationService.DoUpdateAccessToken()
          .then((res) => {
            if (res.status.IsSuccess()) {
              return ReSendApiByNewAccessToken(originalConfig);
            } else {
              location.href = `${window.location.origin}/SessionExpire`;
            }
          })
          .catch((_error) => {
            location.href = `${window.location.origin}/SessionExpire`;
          })
          .finally(() => {
            ProcessQueue();
            isRefreshing = false;
          });
      }

      return Promise.reject(err);
    }
  );
  return instance;
};

const peakExportAPI = CreateAxiosInstance(process.env.VUE_APP_PeakExportService || "");
const peakRealTimeAPIForUser = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeServiceForUser || "");
const peakRealTimeAPI = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeService || "");
const peakPayrollAPI = CreateAxiosInstance(process.env.VUE_APP_PeakPayrollService || "");
const peakTaxAPI = CreateAxiosInstance(process.env.VUE_APP_PeakTaxService || "");
const peakAppConnect = CreateAxiosInstance(process.env.VUE_APP_PeakAppConnectService || "");
const peakAuthen = CreateAxiosInstance(process.env.VUE_APP_PeakAuthenService || "");
const peakNotificationAPI = CreateAxiosInstanceForNotificationService(process.env.VUE_APP_PeakNotification || "");

export default {
  peakExportAPI,
  peakRealTimeAPI,
  peakRealTimeAPIForUser,
  peakPayrollAPI,
  peakAuthen,
  peakNotificationAPI,
};
